import { Info, type LucideIcon } from 'lucide-react'
import React, { useMemo, useLayoutEffect, useRef, useState } from 'react'

import { Briefcase } from 'src/shared/components/icons/briefcase'
import { Language } from 'src/shared/components/icons/language'
import { MapPin } from 'src/shared/components/icons/map-pin'
import { Specialty } from 'src/shared/components/icons/specialty'
import { Badge } from 'src/shared/components/ui/miscellaneous/badge'
import { expertiseMap } from 'src/shared/features/users/schemas/expertise'
import { languageMap } from 'src/shared/features/users/schemas/language'
import type { PublicUserData } from 'src/shared/features/users/schemas/user'
import { cn } from 'src/shared/utils/cn'

type PotentialBadgeData = {
  key: string
  icon: LucideIcon
  text: string
  variant?: 'accent'
}

interface BadgesSectionProps {
  user: PublicUserData
  size?: 'default' | 'small' | 'mini'
  className?: string
  limitLines?: number
  summaryBadgeIcon?: LucideIcon
  measurementContainerClassName?: string
}

export const formatLanguages = (languages: string[], limit = 2) => {
  if (!languages?.length) return ''

  const formattedLanguages = languages
    .slice(0, limit)
    .map((lang) => languageMap[lang]?.label)
    .filter(Boolean)
    .join(', ')

  return languages.length > limit ? `${formattedLanguages} + ${languages.length - limit} more` : formattedLanguages
}

export const getCareerLength = (careerStartDate: string) => {
  const years = new Date().getFullYear() - new Date(careerStartDate).getFullYear()
  if (years < 5) return 'Less than 5 years'
  if (years < 10) return '5-10 years'
  if (years < 15) return '10-15 years'
  if (years < 20) return '15-20 years'
  return '20+ years'
}

export const BadgesSection: React.FC<BadgesSectionProps> = ({
  user,
  size = 'default',
  className,
  limitLines,
  summaryBadgeIcon: SummaryBadgeIcon = Info,
  measurementContainerClassName,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [visibleBadgeCount, setVisibleBadgeCount] = useState<number>(Infinity)
  const [isInitialRender, setIsInitialRender] = useState<boolean>(true)

  const singleValueFields = useMemo(
    () => [
      { label: user.shortAddress || '', icon: MapPin, condition: !!user.shortAddress, keyPrefix: 'address' },
      {
        label: user.careerStartDate ? getCareerLength(user.careerStartDate) : '',
        icon: Briefcase,
        condition: !!user.careerStartDate,
        keyPrefix: 'career',
      },
    ],
    [user.shortAddress, user.careerStartDate],
  )

  const potentialBadges = useMemo<PotentialBadgeData[]>(() => {
    const badges: PotentialBadgeData[] = []

    singleValueFields.forEach((field, index) => {
      if (field.condition) {
        badges.push({
          key: `${field.keyPrefix}-${index}-${field.label}`,
          icon: field.icon,
          text: field.label,
          variant: 'accent',
        })
      }
    })

    if (user.languages && user.languages.length > 0) {
      badges.push({
        key: 'languages',
        icon: Language,
        text: formatLanguages(user.languages, 4),
        variant: 'accent',
      })
    }

    user.expertise?.forEach((expertiseItem) => {
      const expertiseLabel = expertiseMap[user.profession]?.[expertiseItem]?.label
      if (expertiseLabel) {
        badges.push({
          key: `expertise-${expertiseItem}`,
          icon: Specialty,
          text: expertiseLabel,
          variant: 'accent',
        })
      }
    })
    return badges
  }, [user, singleValueFields])

  useLayoutEffect(() => {
    setIsInitialRender(false)

    if (!limitLines || !containerRef.current || size !== 'mini' || potentialBadges.length === 0) {
      setVisibleBadgeCount(potentialBadges.length)
      return
    }

    const children = Array.from(containerRef.current.children) as HTMLElement[]
    if (children.length === 0) {
      setVisibleBadgeCount(0)
      return
    }
    const actualBadgeChildren = children.filter((child) => !child.dataset.summaryBadge)

    if (actualBadgeChildren.length === 0) {
      setVisibleBadgeCount(0)
      return
    }

    const lineTops = new Set<number>()
    actualBadgeChildren.forEach((child) => lineTops.add(child.offsetTop))
    const sortedLineTops = Array.from(lineTops).sort((a, b) => a - b)

    let count = 0
    let maxAllowedOffsetTop = -1

    if (sortedLineTops.length <= limitLines) {
      count = potentialBadges.length
    } else {
      maxAllowedOffsetTop = sortedLineTops[limitLines - 1]
      for (let i = 0; i < actualBadgeChildren.length; i++) {
        if (actualBadgeChildren[i].offsetTop <= maxAllowedOffsetTop) {
          count++
        } else {
          break
        }
      }
    }

    const calculatedHiddenCount = potentialBadges.length - count
    if (calculatedHiddenCount > 0 && count > 0 && actualBadgeChildren[count - 1]) {
      if (actualBadgeChildren[count - 1].offsetTop === maxAllowedOffsetTop) {
        count--
      }
    }

    setVisibleBadgeCount(count)
  }, [potentialBadges, limitLines, size])

  const badgesToRender = isInitialRender && limitLines ? potentialBadges : potentialBadges.slice(0, visibleBadgeCount)
  const hiddenCount = potentialBadges.length - visibleBadgeCount

  return (
    <section
      ref={containerRef}
      className={cn(
        'flex flex-wrap gap-2',
        className,
        isInitialRender &&
          limitLines &&
          measurementContainerClassName &&
          `${measurementContainerClassName} overflow-hidden`,
      )}
    >
      {badgesToRender.map((badge) => (
        <Badge key={badge.key} variant={badge.variant} icon={badge.icon} text={badge.text} size={size} />
      ))}
      {!isInitialRender && hiddenCount > 0 && limitLines && (
        <Badge
          variant="accent"
          icon={SummaryBadgeIcon}
          text={`+${hiddenCount}`}
          size={size}
          data-summary-badge="true"
        />
      )}
    </section>
  )
}

export default BadgesSection
